import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { NavLink, matchPath, useLocation } from 'react-router';
import { CountBadge, Icon, cx, type IconName, type NavItem } from '@saas/ui';
import type { NavGroup, NavModel } from './nav.js';

function NavigationLink({ item, counts }: { item: NavItem; counts: NavModel['counts'] }) {
  const count = item.badge ? counts[item.badge] : 0;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      target={item.newWindow ? '_blank' : undefined}
      rel={item.newWindow ? 'noopener noreferrer' : undefined}
      className={({ isActive }) => cx(
        'flex min-tap w-full items-center gap-2 rounded-input px-3 py-2 text-callout transition-colors',
        isActive ? 'bg-brand-subtle font-semibold text-brand' : 'text-fg-secondary hover:bg-sunken hover:text-fg',
      )}
    >
      <Icon name={item.icon} size={20} className="shrink-0" />
      <span className="min-w-0 flex-1 wrap-name">{item.label}</span>
      {count ? <CountBadge count={count} label={item.label} /> : null}
      {item.newWindow ? <Icon name="open_in_new" size={20} className="shrink-0" /> : null}
    </NavLink>
  );
}

/** 通常のリンクとして Tab で移動できる disclosure。Esc は開いたボタンへ戻す。 */
function NavigationDropdown({ label, icon, active, iconOnly = false, children }: { label: string; icon: IconName; active: boolean; iconOnly?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const ref = useRef<HTMLLIElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <li ref={ref} className="relative" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
    }}>
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={id}
        aria-current={active ? 'true' : undefined}
        aria-label={iconOnly ? label : undefined}
        title={iconOnly ? label : undefined}
        onClick={() => {
          if (!open && button.current) setAlignRight(button.current.getBoundingClientRect().left + 304 > window.innerWidth);
          setOpen((value) => !value);
        }}
        onKeyDown={(event) => {
          if (open && event.key === 'ArrowDown') {
            event.preventDefault();
            panel.current?.querySelector<HTMLElement>('input, a')?.focus();
          }
        }}
        className={cx('flex min-tap items-center whitespace-nowrap rounded-input text-callout', iconOnly ? 'justify-center px-2' : 'gap-2 px-3', active || open ? 'bg-brand-subtle font-semibold text-brand' : 'text-fg-secondary hover:bg-sunken hover:text-fg')}
      >
        <Icon name={icon} size={20} />
        {iconOnly ? null : label}
        {iconOnly ? null : <Icon name={open ? 'expand_less' : 'expand_more'} size={20} />}
      </button>
      {open ? (
        <div
          ref={panel}
          id={id}
          role="region"
          aria-label={`${label}メニュー`}
          className={cx(
            'absolute top-full z-50 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-card border border-line bg-card p-3 shadow-elev-2',
            'max-h-[calc(100dvh-9rem)] overflow-y-auto overscroll-contain',
            alignRight ? 'right-0' : 'left-0',
          )}
          onClick={(event) => {
            if ((event.target as Element).closest('a')) setOpen(false);
          }}
        >
          {children}
        </div>
      ) : null}
    </li>
  );
}

/** ヘッダーと上部ナビゲーションで共用する、業務グループ一つ分のドロップダウン。 */
export function NavigationGroupDropdown({ group, counts, iconOnly = false }: { group: NavGroup; counts: NavModel['counts']; iconOnly?: boolean }) {
  const { pathname } = useLocation();
  const active = group.items.some((item) => Boolean(matchPath({ path: item.to, end: false }, pathname)));
  return (
    <NavigationDropdown label={group.label} icon={group.items[0]!.icon} active={active} iconOnly={iconOnly}>
      <h2 className="px-3 pb-2 text-caption font-medium text-fg-tertiary">{group.label}</h2>
      <ul className="space-y-0.5">
        {group.items.map((item) => <li key={item.to}><NavigationLink item={item} counts={counts} /></li>)}
      </ul>
    </NavigationDropdown>
  );
}

/** 権限判定済みの業務グループを上部に並べ、各グループの項目だけをドロップダウンに出す。 */
export function TopNavigation({ model, groups = model.work }: { model: NavModel; groups?: NavGroup[] }) {
  const [expanded, setExpanded] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const id = useId();
  return (
    <nav
      aria-label="メインメニュー"
      className="top-navigation relative hidden border-t border-line lg:block"
      data-expanded={expanded}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={(event) => { if (!event.currentTarget.contains(document.activeElement)) setExpanded(false); }}
      onFocusCapture={(event) => { if (event.target !== toggle.current) setExpanded(true); }}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setExpanded(false); }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && expanded) {
          setExpanded(false);
          toggle.current?.focus();
        }
      }}
    >
      <div className="top-navigation-panel absolute inset-x-0 top-0 bg-card px-5 py-1">
        <ul id={id} className="top-navigation-groups flex flex-wrap items-center gap-1 pr-28">
          {groups.map((group) => <NavigationGroupDropdown key={group.module} group={group} counts={model.counts} />)}
        </ul>
      </div>
      <button
        ref={toggle}
        type="button"
        aria-expanded={expanded}
        aria-controls={id}
        onClick={() => setExpanded((value) => !value)}
        className="absolute right-5 top-1 z-10 flex min-tap items-center gap-1 rounded-input bg-card px-3 text-callout text-fg-secondary hover:bg-brand-subtle hover:text-brand"
      >
        {expanded ? '閉じる' : 'すべて'}<Icon name={expanded ? 'expand_less' : 'expand_more'} size={20} />
      </button>
    </nav>
  );
}
